import DocumentStore from 'notebook/documents/DocumentStore';
import {Table} from 'notebook/tables/TableConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {AnyStructuredTable, Note, NoteLinkedObjectType} from 'notebook/util/NoteUtilConstants';
import {GREEN, ORANGE, RED} from 'ui-library/Colors';

export interface TablePerformanceSummary {
  tableId: string;
  documentName: string | null;
  name: string;
  numCells: number;
  numRows: number;
  numColumns: number;
  colorHex: string;
  percentage: number;
}

export interface NotePerformanceSummary {
  numDocuments: number;
  numTables: number;
  numCells: number;
  tableSummaries: Array<TablePerformanceSummary>;
}

export const ENTRY_TABLE_CELL_COUNT_LIMIT = 35_000;
const ENTRY_TABLE_CELL_COUNT_LIMIT_WARNING = ENTRY_TABLE_CELL_COUNT_LIMIT * 0.5;

export const _forTesting = {
  getPlainTablePerformanceSummary,
  getStructuredTablePerformanceSummary,
};

function getColorHex(percentage: number): string {
  if (percentage < 0.25) {
    return GREEN;
  } else if (percentage < 0.5) {
    return ORANGE;
  }
  return RED;
}

function getPlainTablePerformanceSummary(table: Table, documentName: string | null): TablePerformanceSummary {
  const numRows = table.table.length;
  const numColumns = table.table[0].length;
  const numCells = numRows * numColumns;
  const percentage = numCells / ENTRY_TABLE_CELL_COUNT_LIMIT;
  const colorHex = getColorHex(percentage);

  return {
    tableId: `${documentName}-${table.name}`,
    documentName,
    name: table.name,
    numCells,
    numRows,
    numColumns,
    colorHex,
    percentage,
  };
}

function getStructuredTablePerformanceSummary(
  table: AnyStructuredTable,
  documentName: string | null
): TablePerformanceSummary {
  const numRows = table.rows.length;
  const numColumns = table.columns.length;
  const numCells = numRows * numColumns;
  const percentage = numCells / ENTRY_TABLE_CELL_COUNT_LIMIT;
  const colorHex = getColorHex(percentage);

  return {
    tableId: `${documentName}-${table.name}`,
    documentName,
    name: table.name,
    numCells,
    numRows,
    numColumns,
    colorHex,
    percentage,
  };
}

export const TableLimitUtils = {
  sizeIsInWarningRange(numCells: number): boolean {
    return numCells > ENTRY_TABLE_CELL_COUNT_LIMIT_WARNING && numCells < ENTRY_TABLE_CELL_COUNT_LIMIT;
  },
  sizeIsOverMaximum(numCells: number): boolean {
    return numCells >= ENTRY_TABLE_CELL_COUNT_LIMIT;
  },

  analyzeNotePerformance(note: Note): NotePerformanceSummary {
    const tables = TableUtil.getTablesFromNote(note);
    return this.analyzePerformanceOfTables({entry: tables});
  },

  analyzePerformanceOfTables(tablesByDocumentName: {
    [documentName: string]: Array<Table | AnyStructuredTable>;
  }): NotePerformanceSummary {
    let numCells = 0;
    let numTables = 0;
    const numDocuments = Object.keys(tablesByDocumentName).length;
    const tableSummaries = Object.entries(tablesByDocumentName).flatMap(([documentName, tables]) =>
      tables.flatMap((table) =>
        // eslint-disable-next-line benchlint/no-in-operator
        'type' in table && table.type === NoteLinkedObjectType.STRUCTURED_TABLE
          ? getStructuredTablePerformanceSummary(table, documentName)
          : getPlainTablePerformanceSummary(table as Table, documentName)
      )
    );

    for (const summary of tableSummaries) {
      numTables++;
      numCells += summary.numCells;
    }

    return {
      numDocuments,
      numTables,
      numCells,
      tableSummaries,
    };
  },
};

export function getTablesByDocumentNameFromDocumentIds(documentIds: Array<string>): {
  [documentName: string]: Array<Table | AnyStructuredTable>;
} {
  const tablesByDocumentName: {[documentName: string]: Array<Table | AnyStructuredTable>} = {};
  for (const documentId of documentIds) {
    const document = DocumentStore.getDocument(documentId);
    if (document) {
      tablesByDocumentName[document.name] = TableUtil.getTablesFromNote(
        document.documentVersion.entryVersion.note
      );
    }
  }
  return tablesByDocumentName;
}
